import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { FileText, Shield, Award, CheckCircle, Download } from 'lucide-react';
const documents = [{
  title: 'Tourism Business License',
  issuer: 'Nepal Tourism Board',
  number: 'NTB-2024-1234',
  validUntil: 'December 2025',
  image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop'
}, {
  title: 'Trekking Agency Registration',
  issuer: 'Ministry of Tourism',
  number: 'TAAN-567890',
  validUntil: 'December 2025',
  image: 'https://images.unsplash.com/photo-1554224311-beee460c201f?q=80&w=2070&auto=format&fit=crop'
}, {
  title: 'Environmental Compliance Certificate',
  issuer: 'Department of National Parks',
  number: 'DNPWC-2024-456',
  validUntil: 'December 2025',
  image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=2070&auto=format&fit=crop'
}, {
  title: 'Safety & Insurance Certificate',
  issuer: 'Nepal Insurance Authority',
  number: 'NIA-2024-789',
  validUntil: 'December 2025',
  image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070&auto=format&fit=crop'
}];
const certifications = [{
  icon: Shield,
  title: 'Fully Licensed & Insured',
  description: 'All necessary permits and comprehensive insurance coverage for operations'
}, {
  icon: Award,
  title: 'Industry Certified',
  description: 'Member of TAAN (Trekking Agencies Association of Nepal) and NMA'
}, {
  icon: CheckCircle,
  title: 'Safety Compliant',
  description: 'Meets all government safety standards and environmental regulations'
}, {
  icon: FileText,
  title: 'Transparent Operations',
  description: 'All documentation available for review and verification'
}];
export function LegalDocumentsPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-1503">
      <Navbar data-id="element-1504" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-1505">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070&auto=format&fit=crop")'
      }} data-id="element-1506">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" data-id="element-1507" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-1508">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1509">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1510">
              <FileText className="text-white" size={32} data-id="element-1511" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-1512">
            Legal Documents
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-slate-100 font-light max-w-2xl mx-auto" data-id="element-1513">
            Fully licensed, certified, and compliant with all regulations
          </motion.p>
        </div>
      </div>

      {/* Overview Section */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1514">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1515">
          <SectionHeading title="Our Credentials" subtitle="Overview" data-id="element-1516" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-1517">
            <p data-id="element-1518">
              Himalayan Trekking operates with full legal authorization and
              maintains all required licenses, permits, and certifications
              mandated by the Government of Nepal. Our commitment to
              transparency and compliance ensures you can trek with complete
              confidence and peace of mind.
            </p>
            <p data-id="element-1519">
              We are proud members of the Trekking Agencies Association of Nepal
              (TAAN) and the Nepal Mountaineering Association (NMA). These
              memberships demonstrate our adherence to industry best practices,
              ethical standards, and safety protocols.
            </p>
            <p data-id="element-1520">
              All our guides hold valid certifications from the Nepal Academy of
              Tourism and Hotel Management (NATHM) and are trained in wilderness
              first aid and high-altitude rescue procedures. We maintain
              comprehensive insurance coverage for all our operations, staff,
              and clients.
            </p>
            <p data-id="element-1521">
              Below you'll find our official licenses and certifications. We
              believe in complete transparency and are happy to provide
              verification of any document upon request.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Certifications Grid */}
      <section className="bg-slate-50 py-20 px-4" data-id="element-1522">
        <div className="max-w-7xl mx-auto" data-id="element-1523">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16" data-id="element-1524">
            {certifications.map((cert, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-md text-center" data-id="element-1525">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-1526">
                  <cert.icon size={28} className="text-white" data-id="element-1527" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-3" data-id="element-1528">
                  {cert.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed" data-id="element-1529">
                  {cert.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-id="element-1530">
        <SectionHeading title="Official Licenses & Certificates" subtitle="Valid and up-to-date documentation" data-id="element-1531" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1532">
          {documents.map((doc, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-1533">
              <div className="relative h-64 overflow-hidden bg-slate-100" data-id="element-1534">
                <img src={doc.image} alt={doc.title} className="w-full h-full object-cover" data-id="element-1535" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-1536" />
                <div className="absolute bottom-4 left-4 right-4" data-id="element-1537">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2" data-id="element-1538">
                    <p className="text-xs text-slate-600 uppercase tracking-wide" data-id="element-1539">
                      License Preview
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-1540">
                <h3 className="text-xl font-bold text-stone-900 mb-4" data-id="element-1541">
                  {doc.title}
                </h3>
                <div className="space-y-2 mb-6" data-id="element-1542">
                  <div className="flex justify-between" data-id="element-1543">
                    <span className="text-sm text-stone-600" data-id="element-1544">Issued by:</span>
                    <span className="text-sm font-semibold text-stone-900" data-id="element-1545">
                      {doc.issuer}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1546">
                    <span className="text-sm text-stone-600" data-id="element-1547">License No:</span>
                    <span className="text-sm font-semibold text-stone-900" data-id="element-1548">
                      {doc.number}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1549">
                    <span className="text-sm text-stone-600" data-id="element-1550">Valid Until:</span>
                    <span className="text-sm font-semibold text-green-600" data-id="element-1551">
                      {doc.validUntil}
                    </span>
                  </div>
                </div>
                <button className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-3 rounded-lg font-medium transition-colors" data-id="element-1552">
                  <Download size={18} data-id="element-1553" />
                  View Full Document
                </button>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Verification Notice */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16 px-4" data-id="element-1554">
        <div className="max-w-4xl mx-auto" data-id="element-1555">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-l-4 border-blue-600" data-id="element-1556">
            <div className="flex items-start gap-4" data-id="element-1557">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shrink-0" data-id="element-1558">
                <Shield className="text-white" size={24} data-id="element-1559" />
              </div>
              <div data-id="element-1560">
                <h3 className="text-2xl font-bold text-stone-900 mb-3" data-id="element-1561">
                  Document Verification
                </h3>
                <p className="text-stone-700 leading-relaxed mb-4" data-id="element-1562">
                  All licenses and certificates displayed on this page are
                  authentic and can be verified through the respective issuing
                  authorities. We encourage you to verify our credentials
                  independently if desired.
                </p>
                <p className="text-stone-700 leading-relaxed" data-id="element-1563">
                  For verification assistance or to request copies of any
                  documents, please contact our office. We're committed to
                  complete transparency in all our operations.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white" data-id="element-1564">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1565">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1566">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-1567">
              Questions About Our Credentials?
            </h2>
            <p className="text-xl text-stone-600 mb-8" data-id="element-1568">
              We're happy to provide additional documentation or answer any
              questions
            </p>
            <a href="#contact" data-id="element-1569">
              <button className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all" data-id="element-1570">
                Contact Us
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1571" />
    </div>;
}